import { useEffect, useState } from "react";
import { IPagination } from "../../../types/pagination";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import { Colaborador } from "../../../services/Colaborador";
import { toast } from "react-toastify";
import { useContextSite } from "../../../context/Context";
import {
  IColaboradorDTO,
  IColaboradorGetProps,
} from "../../../types/colaborador";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { resetValues } from "../../../utils/resetObject";
import { maskCpf } from "../../../utils/masks";
import { useSessionStorage } from "../../../hooks/useSessionStorage";

const schema = z.object({
  nome: z.string().optional().or(z.literal("")),
  cpf: z.string().optional().or(z.literal("")),
});

export const useCollaborators = () => {
  const [pagination, setPagination] = useState<IPagination>({} as IPagination);
  const [numberPage, setNumberPage] = useState(0);
  const isMobile = useMediaQuery({ maxWidth: "640px" });
  const [colaboradores, setColaboradores] = useState<IColaboradorDTO[]>(
    [] as IColaboradorDTO[]
  );
  const { setIsLoad } = useContextSite();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const size = 5;
  const [filter, setFilter] = useState<IColaboradorGetProps>(
    {} as IColaboradorGetProps
  );
  const [usuario] = useSessionStorage("@usuarioMapa");
  const isAdmin = usuario?.roles?.includes("ROLE_ADMIN");

  const canAccessEmployees =
    isAdmin || usuario?.recursos?.includes("CONFIGURACAO_COLABORADOR_VER");
  const canEditEmployees =
    isAdmin || usuario?.recursos?.includes("CONFIGURACAO_COLABORADOR_EDITAR");
  const { register, handleSubmit, reset, watch, setValue } =
    useForm<IColaboradorGetProps>({
      defaultValues: {
        cpf: "",
        nome: "",
      },
      resolver: zodResolver(schema),
    });

  function getColaboradores(props?: IColaboradorGetProps) {
    setIsLoad(true);
    Colaborador.get({ size, ...props })
      .then(({ data }) => {
        setColaboradores(data.content);
        setPagination({
          actualPage: data.number,
          totalPage: data.totalPages,
          totalRegister: data.totalElements,
        });
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => {
          toast.error(mensagem);
        }
      )
      .finally(() => {
        setIsLoad(false);
      });
  }

  function handleReset() {
    reset();
    const empty = resetValues(filter);
    setFilter(empty);
    getColaboradores();
  }

  function handleFilter(data: IColaboradorGetProps) {
    getColaboradores({ page: 0, ...data });
    setNumberPage(0);
  }

  useEffect(() => {
    getColaboradores({ page: numberPage, ...filter });
  }, [numberPage]);

  useEffect(() => {
    if (watch("cpf")) {
      setValue("cpf", maskCpf(watch("cpf")));
    }
  }, [watch("cpf")]);

  return {
    isOpen,
    setIsOpen,
    pagination,
    setNumberPage,
    isMobile,
    navigate,
    colaboradores,
    register,
    handleSubmit,
    handleReset,
    handleFilter,
    canAccessEmployees,
    canEditEmployees,
  };
};
